import { GraphQLErrorCode } from "@kazoohr/types";

import { ApolloError } from "..";

/**
 * Takes a frontend {@link ApolloError} object and determines if the server returned a {@link GraphQLErrorCode.BAD_USER_INPUT} error.
 * If the error extensions contain a path to the field that had the error (like "firstName must be at least 2 characters")
 * it will return that as a {@link FieldErrors} object:
 *
 * @returns
 * ```ts
 * {
 *  firstName: "firstName must be at least 2 characters",
 *  startDate: "StartDate must be before EndDate"
 * }
 * ```
 *
 */
export function parseInputValidationErrors(
  error: ApolloError
): FieldErrors | undefined {
  if (
    !error ||
    !error.graphQLErrors ||
    error.graphQLErrors[0]?.extensions?.code !== GraphQLErrorCode.BAD_USER_INPUT
  ) {
    return;
  }

  const inputErrorObject = error.graphQLErrors
    // We only grab BAD_USER_INPUT errors
    .filter(
      (error) => error.extensions?.code === GraphQLErrorCode.BAD_USER_INPUT
    )
    // We only want the validation property of those BAD_USER_INPUT errors
    // and after that we flatten the array 1 level
    .flatMap((error) => error.extensions?.validation)
    // We create an error object where the LAST error of each path is the one we will show
    .reduce((acc: FieldErrors, item) => {
      const anyItem = item as { path: string; errors: string[] };
      if (!anyItem?.path) {
        return acc;
      }
      Object.assign(acc, {
        [anyItem.path]: anyItem.errors[0],
      });
      return acc;
    }, {});

  return inputErrorObject;
}

export interface FieldErrors {
  [index: string]: string;
}
